import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { API } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/auth/me`, { credentials: "include", headers });
      if (!res.ok) throw new Error("Not authenticated");
      const userData = await res.json();
      setUser(userData);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=") || window.location.pathname === "/auth/callback") {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include", headers });
    } catch {}
    localStorage.removeItem("session_token");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/auth/me`, { credentials: "include", headers });
      if (res.ok) setUser(await res.json());
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refreshUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
